import {
  LineSegment2,
  Point2,
} from '../../../../domain/geometry/geometric-types';
import { SheetShapeDeepFragment } from '../../../../gql/graphql';
import { toPoint2 } from '../../../../domain/geometry/algorithms/util/polygon';
import { getDistanceToSegment } from '../../shapes/util';
import { EdgeMatchResult } from './wedge-types';

// Function to check if two segments are parallel (within tolerance)
export function areSegmentsParallel(
  segment1: LineSegment2,
  segment2: LineSegment2,
  angleTolerance = 0.1
): boolean {
  // Calculate direction vectors
  const dir1: Point2 = [
    segment1[1][0] - segment1[0][0],
    segment1[1][1] - segment1[0][1],
  ];

  const dir2: Point2 = [
    segment2[1][0] - segment2[0][0],
    segment2[1][1] - segment2[0][1],
  ];

  // Normalize the vectors
  const length1 = Math.sqrt(dir1[0] * dir1[0] + dir1[1] * dir1[1]);
  const length2 = Math.sqrt(dir2[0] * dir2[0] + dir2[1] * dir2[1]);

  // Prevent division by zero
  if (length1 < 0.0001 || length2 < 0.0001) {
    return false;
  }

  const normalizedDir1: Point2 = [dir1[0] / length1, dir1[1] / length1];
  const normalizedDir2: Point2 = [dir2[0] / length2, dir2[1] / length2];

  // Calculate dot product
  const dotProduct =
    normalizedDir1[0] * normalizedDir2[0] +
    normalizedDir1[1] * normalizedDir2[1];

  // For parallel lines, the absolute value of the dot product will be close to 1
  // The dot product is the cosine of the angle between the vectors
  return Math.abs(Math.abs(dotProduct) - 1) < angleTolerance;
}

// Find the closest parallel edge to a segment
export function findClosestParallelEdgeToSegment(
  segment: LineSegment2,
  shapes: Record<string, SheetShapeDeepFragment[]>,
  angleTolerance = 0.1,
  unitScale?: number
): EdgeMatchResult {
  let closestEdge: LineSegment2 | null = null;
  let closestShape: SheetShapeDeepFragment | null = null;
  let minDistance = Infinity;
  let isOnLeftSide: boolean | null = null;

  // Get the midpoint of the segment to use for distance calculations
  const segmentMidpoint: Point2 = [
    (segment[0][0] + segment[1][0]) / 2,
    (segment[0][1] + segment[1][1]) / 2,
  ];

  // Calculate direction vector of the segment
  const segmentDirX = segment[1][0] - segment[0][0];
  const segmentDirY = segment[1][1] - segment[0][1];

  // Normalize the segment direction
  const segmentLength = Math.sqrt(
    segmentDirX * segmentDirX + segmentDirY * segmentDirY
  );

  // Prevent division by zero for degenerate segments
  if (segmentLength < 0.0001) {
    return null;
  }

  const normalizedSegmentDirX = segmentDirX / segmentLength;
  const normalizedSegmentDirY = segmentDirY / segmentLength;

  // Calculate a perpendicular vector that points to the LEFT of the segment direction
  // For a vector (dx, dy), the perpendicular vector pointing to the left is (-dy, dx)
  const perpLeftX = -normalizedSegmentDirY;
  const perpLeftY = normalizedSegmentDirX;

  // Check all shapes and their edges
  Object.values(shapes).forEach((shapesArray) => {
    shapesArray.forEach((shape) => {
      // Only consider polygon shapes
      if (shape.sheetShapePolygon) {
        const polygons = shape.sheetShapePolygon.multipolygon.polygons;

        polygons.forEach((polygon) => {
          // Get edges from exterior points
          const exteriorEdges = polygon.exterior.points.map(
            (point, index) =>
              [
                toPoint2(point),
                toPoint2(
                  polygon.exterior.points[
                    (index + 1) % polygon.exterior.points.length
                  ]
                ),
              ] as LineSegment2
          );

          // Check each edge
          exteriorEdges.forEach((edge) => {
            // Only consider parallel edges
            if (areSegmentsParallel(segment, edge, angleTolerance)) {
              const distance = getDistanceToSegment(segmentMidpoint, edge);

              if (distance < minDistance) {
                minDistance = distance;
                closestEdge = edge;
                closestShape = shape;

                // Vector from segment midpoint to edge midpoint
                const edgeMidpoint: Point2 = [
                  (edge[0][0] + edge[1][0]) / 2,
                  (edge[0][1] + edge[1][1]) / 2,
                ];

                const toEdgeX = edgeMidpoint[0] - segmentMidpoint[0];
                const toEdgeY = edgeMidpoint[1] - segmentMidpoint[1];

                // Dot product with left perpendicular vector determines side
                // If dot product is positive, edge is on the left side of the segment
                // If negative, edge is on the right side of the segment
                const dotProduct = toEdgeX * perpLeftX + toEdgeY * perpLeftY;

                // If dot product is positive, edge is on the left side
                isOnLeftSide = dotProduct > 0;
              }
            }
          });

          // Also check interior rings if they exist
          polygon.interiors.forEach((interiorRing) => {
            const interiorEdges = interiorRing.points.map(
              (point, index) =>
                [
                  toPoint2(point),
                  toPoint2(
                    interiorRing.points[
                      (index + 1) % interiorRing.points.length
                    ]
                  ),
                ] as LineSegment2
            );

            interiorEdges.forEach((edge) => {
              // Only consider parallel edges
              if (areSegmentsParallel(segment, edge, angleTolerance)) {
                const distance = getDistanceToSegment(segmentMidpoint, edge);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestEdge = edge;
                  closestShape = shape;

                  // Vector from segment midpoint to edge midpoint
                  const edgeMidpoint: Point2 = [
                    (edge[0][0] + edge[1][0]) / 2,
                    (edge[0][1] + edge[1][1]) / 2,
                  ];

                  const toEdgeX = edgeMidpoint[0] - segmentMidpoint[0];
                  const toEdgeY = edgeMidpoint[1] - segmentMidpoint[1];

                  // Dot product with left perpendicular vector determines side
                  // If dot product is positive, edge is on the left side of the segment
                  // If negative, edge is on the right side of the segment
                  const dotProduct = toEdgeX * perpLeftX + toEdgeY * perpLeftY;

                  // If dot product is positive, edge is on the left side
                  isOnLeftSide = dotProduct > 0;
                }
              }
            });
          });
        });
      }
    });
  });

  if (closestEdge && closestShape) {
    // Calculate if distance is less than 100mm when unitScale is provided
    const isCloserThan100mm = unitScale ? minDistance * unitScale < 0.1 : false;

    return {
      edge: closestEdge,
      shape: closestShape,
      distance: minDistance,
      isCloserThan100mm,
      isOnLeftSide,
    };
  }

  return null;
}
